import { DateTime } from "luxon";
import type { AdminDailySalesDB } from "@KassaroAB/shared/types/adminDailySales";
import type { Money } from "@KassaroAB/shared/types/money";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import colors from "@styling/colors";

export const SALES_PIE_COLORS = [
  colors.primary,
  colors.secondary,
  colors.success,
  colors.info,
  colors.warning,
  colors.danger,
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
];

export const SALES_TIMEZONE = "Europe/Stockholm";
export const MAX_SALES_PERIOD_DAYS = 30;

export const getSalesToday = (): DateTime => {
  return DateTime.now().setZone(SALES_TIMEZONE).startOf("day");
};

export type SalesPeriodPreset = "thisMonth" | "lastMonth" | "last30Days" | "custom";

export const getPeriodDayCount = (from: string, to: string): number => {
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  const end = DateTime.fromISO(to, { zone: SALES_TIMEZONE }).startOf("day");
  if (!start.isValid || !end.isValid) {
    return MAX_SALES_PERIOD_DAYS;
  }
  return Math.max(1, Math.round(end.diff(start, "days").days) + 1);
};

export const clampPeriodRange = (
  from: string,
  to: string
): { from: string; to: string } => {
  const today = getSalesToday();
  let start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  let end = DateTime.fromISO(to, { zone: SALES_TIMEZONE }).startOf("day");

  if (!start.isValid || !end.isValid) {
    const fallbackToday = getSalesToday();
    return {
      from: fallbackToday
        .minus({ days: MAX_SALES_PERIOD_DAYS - 1 })
        .toFormat("yyyy-MM-dd"),
      to: fallbackToday.toFormat("yyyy-MM-dd"),
    };
  }

  if (end > today) {
    end = today;
  }
  if (start > end) {
    start = end;
  }

  const dayCount = getPeriodDayCount(
    start.toFormat("yyyy-MM-dd"),
    end.toFormat("yyyy-MM-dd")
  );
  if (dayCount > MAX_SALES_PERIOD_DAYS) {
    start = end.minus({ days: MAX_SALES_PERIOD_DAYS - 1 });
  }

  return {
    from: start.toFormat("yyyy-MM-dd"),
    to: end.toFormat("yyyy-MM-dd"),
  };
};

export const getPeriodRangeForPreset = (
  preset: Exclude<SalesPeriodPreset, "custom">
): { from: string; to: string } => {
  const today = getSalesToday();
  if (preset === "last30Days") {
    return clampPeriodRange(
      today.minus({ days: MAX_SALES_PERIOD_DAYS - 1 }).toFormat("yyyy-MM-dd"),
      today.toFormat("yyyy-MM-dd")
    );
  }
  if (preset === "thisMonth") {
    return clampPeriodRange(
      today.startOf("month").toFormat("yyyy-MM-dd"),
      today.toFormat("yyyy-MM-dd")
    );
  }
  const lastMonthEnd = today.startOf("month").minus({ days: 1 });
  return clampPeriodRange(
    lastMonthEnd.startOf("month").toFormat("yyyy-MM-dd"),
    lastMonthEnd.toFormat("yyyy-MM-dd")
  );
};

export const getDefaultPeriodRange = (): { from: string; to: string } =>
  getPeriodRangeForPreset("last30Days");

export const formatPeriodLabel = (from: string, to: string): string => {
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE });
  const end = DateTime.fromISO(to, { zone: SALES_TIMEZONE });
  return `${start.toFormat("d MMM")} – ${end.toFormat("d MMM yyyy")}`;
};

export const getLastDayInPeriod = (from: string, to: string): string => {
  const today = getSalesToday();
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  const end = DateTime.fromISO(to, { zone: SALES_TIMEZONE }).startOf("day");
  if (!start.isValid || !end.isValid) {
    return today.toFormat("yyyy-MM-dd");
  }
  const clampedEnd = end > today ? today : end;
  return clampedEnd < start
    ? start.toFormat("yyyy-MM-dd")
    : clampedEnd.toFormat("yyyy-MM-dd");
};

export const getDefaultSelectedDateInPeriod = (
  from: string,
  to: string
): string => {
  const today = getSalesToday();
  const yesterday = today.minus({ days: 1 });
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  const end = DateTime.fromISO(to, { zone: SALES_TIMEZONE }).startOf("day");
  if (!start.isValid || !end.isValid) {
    return yesterday.toFormat("yyyy-MM-dd");
  }
  const clampedEnd = end > today ? today : end;
  if (yesterday >= start && yesterday <= clampedEnd) {
    return yesterday.toFormat("yyyy-MM-dd");
  }
  return getLastDayInPeriod(from, to);
};

export const formatSalesDateLabel = (date: string): string => {
  return DateTime.fromISO(date, { zone: SALES_TIMEZONE }).toFormat("d MMM yyyy");
};

export const isSalesToday = (date: string): boolean => {
  return date === getSalesToday().toFormat("yyyy-MM-dd");
};

export const clampDateToPeriod = (
  date: string,
  from: string,
  to: string
): string => {
  const target = DateTime.fromISO(date, { zone: SALES_TIMEZONE }).startOf("day");
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  const end = DateTime.fromISO(to, { zone: SALES_TIMEZONE }).startOf("day");
  if (!target.isValid) {
    return getDefaultSelectedDateInPeriod(from, to);
  }
  if (target < start) {
    return from;
  }
  if (target > end) {
    return to;
  }
  return date;
};

export const getPrimaryGrossAmount = (
  grossAmountByCurrency: AdminDailySalesDB["grossAmountByCurrency"]
): Money | null => getMoneyForCurrency(grossAmountByCurrency, Currency.SEK);

export const getMoneyForCurrency = (
  byCurrency: Record<string, Money>,
  currency: string
): Money | null => {
  return (
    byCurrency[currency] ??
    Object.values(byCurrency).find((m) => m.currency === currency) ??
    null
  );
};

export const aggregatePeriodDailySales = (
  dailyStats: AdminDailySalesDB[]
): { grossAmount: Money | null } => {
  let totalAmount = 0;
  let currency: Currency | null = null;

  for (const day of dailyStats) {
    const gross = getPrimaryGrossAmount(day.grossAmountByCurrency);
    if (!gross) {
      continue;
    }
    totalAmount += gross.amount;
    currency = gross.currency;
  }

  return {
    grossAmount: currency ? { amount: totalAmount, currency } : null,
  };
};

const isMonthPreset = (preset: SalesPeriodPreset) =>
  preset === "thisMonth" || preset === "lastMonth";

export const getComparisonDate = (
  date: string,
  preset: SalesPeriodPreset,
  period: { from: string; to: string }
): string => {
  const day = DateTime.fromISO(date, { zone: SALES_TIMEZONE }).startOf("day");
  if (!isMonthPreset(preset)) {
    return day
      .minus({ days: getPeriodDayCount(period.from, period.to) })
      .toFormat("yyyy-MM-dd");
  }
  const previousMonth = day.minus({ months: 1 });
  const isLastDayOfMonth = day.hasSame(day.endOf("month"), "day");
  return (isLastDayOfMonth ? previousMonth.endOf("month") : previousMonth)
    .startOf("day")
    .toFormat("yyyy-MM-dd");
};

export const getComparisonPeriod = (
  preset: SalesPeriodPreset,
  period: { from: string; to: string }
): { from: string; to: string } => ({
  from: getComparisonDate(period.from, preset, period),
  to: getComparisonDate(period.to, preset, period),
});

export const getDatesInPeriod = (from: string, to: string): string[] => {
  const start = DateTime.fromISO(from, { zone: SALES_TIMEZONE }).startOf("day");
  return Array.from({ length: getPeriodDayCount(from, to) }, (_, index) =>
    start.plus({ days: index }).toFormat("yyyy-MM-dd")
  );
};
